import {FishTypeTable} from 'components/InputMenu/Concession/FishTypeTable';
import {AssignTree} from 'components/InputMenu/Concession/AssignTree';
import InputActionButtons from './InputActionButtons';
import useAddEditConcession from 'hooks/addEdit/useAddEditConcession';
import {useGetPlan} from 'hooks/swr';
import useIsMounted from 'hooks/useIsMounted';
import useParseURL from 'hooks/useParseURL';
import useGetPlanTreeNavigation from 'hooks/plan/useGetPlanTreeNavigation';
import GenericTextField from 'components/Generic/TextField';
import {useTranslation} from 'react-i18next';
import {Checkbox, FormControlLabel, Stack, Divider} from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import {MABTable} from './Concession/MABTable';
import {CompanySelector} from 'components/Shared/CompanySelector';
import {FishGroupPropertiesTags} from 'components/Shared/FishGroupPropertiesTags';
import {useFlags} from 'launchdarkly-react-client-sdk';

const EditMenuContainer = () => {
  const {t} = useTranslation('common');
  const {planId} = useParseURL();
  const {concession, setConcession, selectedSitesCages, setSelectedSitesCages, setCreateUpdateConcession, isUpdating} =
    useAddEditConcession();
  const isMounted = useIsMounted();
  const {data: plan} = useGetPlan({resourceId: planId});
  const {planTreeNavigation} = useGetPlanTreeNavigation({planId, isMounted});
  const {ownership: ownershipEnabled} = useFlags();
  const mabIsFormValid = concession.mab?.length
    ? concession.mab.every(({limit, fromDate}) => limit !== undefined && !!fromDate)
    : false;
  const isFormValid =
    mabIsFormValid && concession.name && concession.regionId && (ownershipEnabled ? concession.companyId : true);

  return (
    <form
      onKeyPress={(event) => {
        if (event.key === 'Enter' && isFormValid) {
          setCreateUpdateConcession(true);
        }
      }}
      noValidate
      autoComplete="off"
      style={{width: '100%'}}
    >
      <Stack direction="column" spacing={2}>
        {/* Concession Name */}
        <GenericTextField
          inputProps={{'data-testid': 'concessions-menu-name'}}
          label={t('name')}
          value={concession.name}
          onChange={(e) => {
            setConcession({...concession, name: e.target.value});
          }}
        />
        {/* Company */}
        {ownershipEnabled && (
          <CompanySelector
            planId={planId}
            companyId={concession.companyId}
            setCompanyId={(companyId) => {
              setConcession({...concession, companyId});
            }}
          />
        )}
        <Divider />
        {/* Concession MAB Id */}
      </Stack>
      <ListSubheader disableSticky={true}>{t('mabLevels')}</ListSubheader>

      {plan && (
        <MABTable
          mabs={concession.mab ?? []}
          minDate={new Date(plan.startDate)}
          maxDate={new Date(plan.endDate)}
          onChange={(mab) => {
            setConcession({...concession, mab});
          }}
        />
      )}
      <Stack margin={2}>
        {/* Whole Cage Assignment */}
        <FormControlLabel
          control={
            <Checkbox
              checked={concession.wholeCageAssignment ?? false}
              onChange={({target: {checked}}) => {
                setConcession({...concession, wholeCageAssignment: checked});
              }}
            />
          }
          label={t('onlyAssingWholeCages')}
        />
      </Stack>
      {/* Fish Type Id */}
      <FishTypeTable concession={concession} setConcession={setConcession} />

      {/* Cages Id */}
      {planTreeNavigation && (
        <AssignTree
          planTreeNavigation={planTreeNavigation}
          concession={concession}
          setConcession={setConcession}
          selected={selectedSitesCages}
          setSelected={setSelectedSitesCages}
        />
      )}

      <FishGroupPropertiesTags
        sx={{my: 1}}
        selectedTags={concession.requiredFishGroupPropertyIds}
        onAfterCreate={(newProperty) => {
          setConcession({
            ...concession,
            requiredFishGroupPropertyIds: [...concession.requiredFishGroupPropertyIds, newProperty],
          });
        }}
        onSelectionChange={(selectedFishGroupProperties) => {
          setConcession({...concession, requiredFishGroupPropertyIds: selectedFishGroupProperties});
        }}
        label={t('requiredFishGroupProperties')}
        helperText={t('concessionRequiredFishGroupPropertiesHelperText')}
      />

      {/* Description */}
      <GenericTextField
        sx={{my: 2}}
        inputProps={{'data-testid': 'concessions-menu-description'}}
        multiline
        label={t('comment')}
        value={concession.comment}
        onChange={(e) => {
          setConcession({...concession, comment: e.target.value});
        }}
      />
      <InputActionButtons
        disabled={!isFormValid || isUpdating}
        loading={isUpdating}
        setCreateUpdateInput={setCreateUpdateConcession}
      />
    </form>
  );
};

export default EditMenuContainer;
